import {
    AttributeDefinitionType,
    AttributesData,
    Conversationsv2DataType,
    NotificationPropsType,
    TaskAttribute,
    TaskMode,
    UpdatedAttributesData,
} from 'Types/Tasks.types'
import { AttributeContext } from '../AttributeManager'
import {
    AttributeGroupConfigExtended,
    AttributeGroupEvent,
    BaseAttributeGroup,
} from './BaseAttributeGroup'
import { forEach, isEmpty } from 'lodash'
import { AttributeValueType } from 'Types/Attribute.types'
import { FormInstance } from 'antd/lib'
import { createReactElement } from 'shared/utils/react.utils'
import { AttributesFormWrapper } from 'Components/tasks/Attributes/AttributesFormWrapper'
import { convertFlatToWrappedAttributes, showAttribute } from 'Utils/attributes.utils'

export class TurnTargetedAttributeGroup extends BaseAttributeGroup {
    attributeDefinitionType = AttributeDefinitionType.TURN_TARGETED_ATTRIBUTES as const
    constructor(attributeContext: AttributeContext, config: AttributeGroupConfigExtended) {
        super(attributeContext, config)
        const draftOutputContainer = this.attributeContext.taskState?.getDraftOutputContainer()
        if (!draftOutputContainer) {
            return
        }
        const data = draftOutputContainer.data.data as Conversationsv2DataType
        this.updateAreMandatoryAttributesFilled(
            data.response.main_info.value.json_attributes?.[this.groupKey!]
        )
        this.build()
    }

    public build(): void {
        const config = this.attributeContext.taskConfig

        const props = {
            groupKey: this.groupKey,
            attributes: this.attributes,
            onUpdateAttribute: (attrName: string, val: AttributeValueType) => {
                const draftOutputContainer =
                    this.attributeContext.taskState?.getDraftOutputContainer()
                if (!draftOutputContainer) {
                    return
                }
                const data = draftOutputContainer.data.data as Conversationsv2DataType

                const jsonAttributes = data.response.main_info.value.json_attributes ?? {}
                if (val === undefined) {
                    if (jsonAttributes?.[this.groupKey!]?.[attrName]) {
                        delete jsonAttributes?.[this.groupKey!]?.[attrName]
                        if (
                            Object.keys(jsonAttributes?.[this.groupKey!]?.attributes ?? {})
                                .length === 0
                        ) {
                            delete jsonAttributes?.[this.groupKey!]
                        }
                    }
                } else {
                    if (jsonAttributes?.[this.groupKey!]?.[attrName]) {
                        jsonAttributes![this.groupKey!]![attrName].value = val
                    } else {
                        this.initJSONPathAttribute(attrName, val, this.attributes)
                    }
                }

                this.emit(AttributeGroupEvent.Change)

                this.attributeContext.saveTask({
                    onComplete: (notifications?: NotificationPropsType) =>
                        this.attributeContext.studio.exitTask(notifications),
                })

                this.updateAreMandatoryAttributesFilled(
                    (
                        this.attributeContext.taskState?.getDraftOutputContainer()?.data
                            .data as Conversationsv2DataType
                    ).response.main_info.value.json_attributes?.[this.groupKey!]
                )
                this.validate()
            },
            getValues: this.getAttributeValues,
            attributesOnUI: this.attributeContext.attributesOnUI,
            setForm: (form: FormInstance) => {
                this.form = form
            },
        }

        this.element = createReactElement(AttributesFormWrapper, {
            ...props,
            isReadOnly: this.isReadOnly,
            headerComponent:
                this.props?.header ?? this.attributeContext.task.createAttributeHeader(),
            getFeedback: this.attributeContext.task.contextHandler.getFeedback,
            mode: config.mode,
            isTrainingMode: this.attributeContext.task.contextHandler.isTrainingMode,
        })
    }

    private getAttributeValues = () => {
        const draftOutputContainer = this.attributeContext.taskState?.getDraftOutputContainer()
        if (!draftOutputContainer) {
            return
        }
        const data = draftOutputContainer.data.data as Conversationsv2DataType
        const jsonAttributes = data.response.main_info.value.json_attributes ?? {}

        let attrs: AttributesData = {}

        const turnId = this.groupKey?.match(/^\$\.turn\[\d+\]/)?.[0]
        const currentTurn = turnId ?? null

        attrs = Object.keys(jsonAttributes ?? {}).reduce((acc, path: string) => {
            // Only include paths that match the current turn
            if (currentTurn && path.startsWith(currentTurn)) {
                forEach(jsonAttributes?.[path], (attrValue, key) => {
                    acc[`${key}`] = {
                        data: attrValue,
                    }
                })
            }
            return acc
        }, {} as AttributesData)

        return attrs
    }

    private initJSONPathAttribute(
        attrName: string,
        val: NonNullable<AttributeValueType>,
        attributes: TaskAttribute[]
    ) {
        const draftOutputContainer = this.attributeContext.taskState?.getDraftOutputContainer()
        if (!draftOutputContainer) {
            return
        }
        const data = draftOutputContainer.data.data as Conversationsv2DataType

        if (isEmpty(data.response.main_info.value.json_attributes)) {
            data.response.main_info.value.json_attributes = {}
        }

        if (isEmpty(data.response.main_info.value.json_attributes?.[this.groupKey])) {
            data.response.main_info.value.json_attributes![this.groupKey] = {}
        }

        data.response.main_info.value.json_attributes![this.groupKey!]![attrName] = {
            value_id: crypto.randomUUID(),
            value: val,
            value_type: attributes.find(attr => attr.name === attrName)!.value_definition
                .value_type,
        }
    }

    validate = async () => {
        let isValidData = true

        try {
            await this.form?.validateFields()
        } catch (e: unknown) {
            const errorFields = (e as { errorFields: unknown[] }).errorFields
            isValidData = errorFields.length === 0
        }

        if (isValidData) {
            this.attributeContext.studio.inputManager.removeViolation(
                `turn-json-attributes-${this.groupKey}`,
                'error'
            )
        } else {
            this.attributeContext.studio.inputManager.addViolation({
                id: `turn-json-attributes-${this.groupKey}`,
                type: 'error',
                message: `Please fill the turn level JSON attribute for ${this.groupKey}`,
            })
        }

        this.render()

        return isValidData
    }

    getVisibleMandatoryAttributes = (attributesData: AttributesData) => {
        const attributesOnUI: { [key: string]: 1 } = {}
        const toUpdateRef = { current: {} }

        return this.mandatoryAttributes.filter(attr => {
            const isVisible = showAttribute(
                this.mandatoryAttributes,
                attr,
                attributesOnUI,
                attributesData,
                toUpdateRef
            )
            attributesOnUI[attr.name] = 1
            return isVisible
        })
    }

    updateAreMandatoryAttributesFilled(attributesData?: UpdatedAttributesData) {
        if (!this.mandatoryAttributes.length) {
            this.areMandatoryAttributesFilled = true
            return
        }

        if (!attributesData) {
            this.areMandatoryAttributesFilled = false
            return
        }

        const wrappedAttributesData = convertFlatToWrappedAttributes(attributesData)
        const visibleMandatoryAttributes = this.getVisibleMandatoryAttributes(wrappedAttributesData)

        this.areMandatoryAttributesFilled = !visibleMandatoryAttributes.some(attr => {
            if (!attributesData[attr.name]) {
                return true
            }
            return !(attributesData as UpdatedAttributesData)[attr.name].value
        })
    }

    removeAllAttributes() {
        const data = this.attributeContext.taskState.getDraftOutputContainer()?.data
            .data as Conversationsv2DataType
        if (!data) {
            return
        }
        const groupKey = this.groupKey
        if (data.response.main_info.value.json_attributes?.[groupKey]) {
            delete data.response.main_info.value.json_attributes![groupKey]
        }
    }

    private isReadOnly = () => {
        const config = this.attributeContext.taskConfig

        if (config.mode === TaskMode.Triager) {
            return false
        } else if (config.mode === TaskMode.Maker || config.mode === TaskMode.Rework) {
            return this.attributeContext.task.isReadOnly || this.disabled
        } else {
            return this.attributeContext.task.isReadOnly
        }
    }
}
